import { t } from '@/locales/i18n'
import { type ID } from '@/types/basic'
import { type GwScrollContProps } from '@/components/lib/GwScrollCont/GwScrollCont.vue'
import type { GwPaginationProps } from '@/components/lib/GwPagination/gwPaginationUtils'
import type {
  TUiType,
  ReportTableRowKeys,
  NewReportTableRowKeys,
  UiColor,
} from '@/types/api/reports'
import type { CSSProperties } from 'vue'
import type { OrderParams } from '@/store/api/gql/hands/queries'

export type GwTableColumn<T> = {
  field: T
  id: ID
  uiType: TUiType
  name?: string
  sortKey?: string
  width?: string
  severities?: UiColor[]
  subColumns?: (GwTableColumn<T> & { subColumn: string })[]
  subColumn?: string
  isDragging?: boolean
}

const columnStyles = {
  uiType: {
    date: {
      width: '8rem',
    },
    boards: {
      width: '8rem',
    },
  },
  fieldName: {
    avg_ev_loss: {
      width: '8rem',
    },
    avg_gto_score: {
      width: '10rem',
    },
  },
}

export const extractColumnStyles = (uiType: TUiType, fieldName: string): CSSProperties =>
  columnStyles.uiType[uiType] || columnStyles.fieldName[fieldName] || {}

export interface GwTableSortConf<TColumn> {
  sortOrder?: 'ASC' | 'DESC'
  sortKey?: GwTableColumn<TColumn>['sortKey']
}

export const tableVariantsConf = {
  table_default: {
    table: {
      variant: 'box_table_default',
    },
    head: {
      variant: 'box_table_header',
      th: {
        variant: 'box_table_header_th',
      },
    },
    row: {
      gap: 0,
      tr: {
        variant: 'box_table_row',
        height: 3,
      },
    },
  },
  table_fat: {
    table: {
      variant: 'box_table_fat',
    },
    head: {
      variant: 'box_table_header_underlined',
      th: {
        variant: 'box_table_header_underlined_th',
      },
    },
    row: {
      gap: 1,
      tr: {
        variant: 'box_table_row_fat',
        height: 8,
      },
    },
  },
} as const

export type GwTableVariant = keyof typeof tableVariantsConf
export type GwTableVariantConf = (typeof tableVariantsConf)[GwTableVariant]
export type GwTableVariantHeadTh = GwTableVariantConf['head']['th']['variant']
export type GwTableVariantRowTr = GwTableVariantConf['row']['tr']['variant']

export interface GwTableProps<TItem, TColumn> {
  idKey: keyof TItem
  variant: GwTableVariant
  items: TItem[]
  columns: GwTableColumn<TColumn>[]
  loading?: boolean
  sortConf?: GwTableSortConf<GwTableColumn<TColumn>>
  count?: number
  error?: object | null
  gwPaginationProps?: GwPaginationProps
  gwScrollContProps?: GwScrollContProps
  hasCheckboxes?: boolean
  hasNotes?: boolean
  hasColors?: boolean
  hasNext?: boolean
  hasPagination?: boolean
  tstPrefix?: string
}

export type GwTableCheckBoxState = 'all' | 'some' | 'none'

export const getOrderingParams = <T extends string>(sortConf: GwTableSortConf<T>) => {
  if (!sortConf.sortKey) return undefined
  return sortConf.sortOrder === 'ASC'
    ? (sortConf.sortKey as OrderParams<T>)
    : (`-${sortConf.sortKey}` as OrderParams<T>)
}

// https://api.dev.gtowizard.com/gto-wizard-admin/hh_filters/handcolumn/
export type TReportTableColumn = GwTableColumn<ReportTableRowKeys>

export type TNewReportTableColumn = GwTableColumn<NewReportTableRowKeys>

export const newReportTableColumns = [
  { field: 'name', name: t(`hhv3.reports`) },
  {
    field: 'last_viewed',
    name: t(`hhv3.last_viewed`),
    uiType: 'date',
  },
].map(i => ({ ...i, id: i.field })) as TNewReportTableColumn[]

const severityColors: Record<UiColor['severity'], string> = {
  severity1: 'var(--app-clr-best_move)',
  severity2: 'var(--app-clr-inaccuracy)',
  severity3: 'var(--app-clr-blunder)',
}

export const severityToColor = (severities?: UiColor[]) => (value: number) => {
  if (!severities) return severityColors.severity1

  const currentSeverity = severities.find(({ from, to }) => value >= from && value <= to)
  if (!currentSeverity) return 'var(--clr-white)'

  return severityColors[currentSeverity.severity]
}

export const getGameTableTitle = (gameTable: string) => {
  const regex = /^\d+/
  const match = gameTable.match(regex)
  if (!match) return ''
  if (match[0] === '2') return 'Heads-Up'
  return match && `${match[0]}-max`
}
